import React, { useEffect, useState } from 'react';
import WizardFrame from './WizardFrame';
import { INTEGRATIONS_LOGO_PATH } from '../common/constants';
import { sendPmsRequest } from '../../utils/api_integrations';

// ---------------------------------------------------------------- #
// PmsWizard
// ---------------------------------------------------------------- #
//
// The RMS / Newbook wizard for one park (plan 09-pms.md):
//   1. Choose your PMS: RMS or Newbook tiles. Nothing is saved yet.
//   2. Request access: the email that will go out (from a dry run of
//      pms/send-request), an optional note, and "Send request".
//   3. Done: the confirmation. A park already in review opens here, with
//      the note that says when the request was sent, and "Send again".
// Marking PMS "not needed" (or needed again) is the switch in the frame's footer.
//
// Props (the same contract as every provider wizard):
//   register       The `data` of GET register
//   parkiId        The park the wizard was opened from
//   initialStep    The step to open at (ignored here; the park's status decides)
//   connectionKey  Unused by PMS
//   canConnect     Unused by PMS (no sign-in to a provider)
//   onClose        Called with true when something was saved, false otherwise

// ---------------------------------------------------------------- #
// Constants
// ---------------------------------------------------------------- #

/** The three step labels. */
const PMS_STEPS = ['Choose your PMS', 'Request access', 'Done'];

/** The two PMS providers the register supports, with their logos. */
const PMS_OPTIONS = [
  { application: 'RMS', logo: `${INTEGRATIONS_LOGO_PATH}/rms.png` },
  { application: 'Newbook', logo: `${INTEGRATIONS_LOGO_PATH}/newbook.png` }
];

/** Register status number for "Admin Review" (request sent, waiting on staff). */
const STATUS_ADMIN_REVIEW = 1;

/** Longest note the API keeps; it cuts anything longer. */
const NOTE_MAX_LENGTH = 1000;

/** Warning shown before resending for a park whose PMS is already set up. */
const WAS_COMPLETE_WARNING = "This park's PMS is currently set up. Sending a new request will mark it as in review until Parki support confirms.";

// ---------------------------------------------------------------- #
// The wizard
// ---------------------------------------------------------------- #

/**
 * The three-step RMS / Newbook access request wizard for one park.
 */
const PmsWizard = ({ register, parkiId, onClose }) => {

  // ---------------------------------------------------------------- #
  // Find the park and its PMS cell
  // ---------------------------------------------------------------- #

  // Find the park the wizard was opened for
  let park = null;
  for (const candidate of register?.parks || []) {
    if (String(candidate.parkiId) === String(parkiId)) {
      park = candidate;
      break;
    }
  }

  // Read the PMS cell, and what is already chosen
  const pmsCell = park?.cells?.pms || {};
  const alreadyInReview = pmsCell.onboardStatus === STATUS_ADMIN_REVIEW;
  const currentApplication = pmsCell.mapping?.application || null;

  // ---------------------------------------------------------------- #
  // State
  // ---------------------------------------------------------------- #

  // A park already in review opens on the Done step
  let openingStep = 1;
  if (alreadyInReview) {
    openingStep = 3;
  }

  const [step, setStep] = useState(openingStep);
  const [maxReachedStep, setMaxReachedStep] = useState(openingStep);
  const [application, setApplication] = useState(currentApplication);
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [changed, setChanged] = useState(false);

  // ---------------------------------------------------------------- #
  // Step navigation
  // ---------------------------------------------------------------- #

  /**
   * Show a step and remember it as reached.
   */
  const goToStep = (stepNumber) => {
    setError(null);
    setStep(stepNumber);
    if (stepNumber > maxReachedStep) {
      setMaxReachedStep(stepNumber);
    }
  };

  // ---------------------------------------------------------------- #
  // Step 2: load the email preview
  // ---------------------------------------------------------------- #

  // Fetch a dry-run preview of the email whenever step 2 opens for a chosen PMS
  useEffect(() => {
    // Only preview on step 2 with a PMS chosen
    const needsPreview = step === 2 && application;
    if (!needsPreview) {
      return;
    }

    // Ask the API to build the email without sending it
    let cancelled = false;
    const loadPreview = async () => {
      setPreviewLoading(true);
      setPreview(null);
      try {
        const result = await sendPmsRequest(park.parkiId, application, '', true);
        if (!cancelled) {
          setPreview(result.data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'The email preview could not be loaded.');
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      }
    };
    loadPreview();

    return () => {
      cancelled = true;
    };
  }, [step, application, park?.parkiId]);

  // ---------------------------------------------------------------- #
  // Actions
  // ---------------------------------------------------------------- #

  /**
   * Send the access request email for real, then show the Done step.
   */
  const handleSend = async () => {
    setBusy(true);
    setError(null);
    try {
      // Send the request with the admin's note
      const result = await sendPmsRequest(park.parkiId, application, note, false);

      // Remember the result and that the register changed
      setSendResult(result.data);
      setChanged(true);
      setNote('');
      goToStep(3);
    } catch (err) {
      setError(err.message || 'The email could not be sent. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Start again from step 2 with the PMS already chosen, to resend the request.
   */
  const handleSendAgain = () => {
    setSendResult(null);
    if (!application) {
      goToStep(1);
      return;
    }
    goToStep(2);
  };

  /**
   * Close the wizard, telling the page whether anything was saved.
   */
  const handleClose = () => {
    onClose(changed);
  };

  // ---------------------------------------------------------------- #
  // Park not found
  // ---------------------------------------------------------------- #

  // Guard against a park id that is not in the register
  if (!park) {
    return (
      <WizardFrame title="PMS" steps={[]} onClose={() => onClose(false)}>
        <p className="mb-0">That park is not in your Integrations Register.</p>
      </WizardFrame>
    );
  }

  // ---------------------------------------------------------------- #
  // Step 1: choose your PMS
  // ---------------------------------------------------------------- #

  /**
   * Draw the RMS and Newbook tiles.
   */
  const renderChooseStep = () => {
    // Build one large tile per PMS provider
    const tiles = [];
    for (const option of PMS_OPTIONS) {
      const isSelected = application === option.application;
      let tileClass = 'integrations-provider-tile';
      if (isSelected) {
        tileClass += ' is-selected';
      }
      tiles.push(
        <div key={option.application} className="col-sm-6">
          <button
            type="button"
            className={tileClass}
            onClick={() => setApplication(option.application)}
            aria-pressed={isSelected}
          >
            <img src={option.logo} alt={option.application} />
            <span className="mt-2 fw-medium">{option.application}</span>
          </button>
        </div>
      );
    }

    return (
      <>
        <p className="text-center text-body-secondary mb-4">Which property management system does this park use?</p>

        {/* Explain a legacy No Integration status set by staff */}
        {pmsCell.reason && (
          <div className="alert alert-secondary" role="status">{pmsCell.reason}</div>
        )}

        <div className="row g-3">
          {tiles}
        </div>
      </>
    );
  };

  // ---------------------------------------------------------------- #
  // Step 2: request access
  // ---------------------------------------------------------------- #

  /**
   * Draw what happens next, the email preview and the note box.
   */
  const renderRequestStep = () => {
    // Wait for the preview before showing the email
    if (previewLoading || !preview) {
      return (
        <div className="text-center py-5">
          {previewLoading && (
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          )}
        </div>
      );
    }

    // Read the email the API will send
    const email = preview.email || {};
    const toList = (email.to || []).join(', ');
    const ccList = (email.cc || []).join(', ');

    // Split the body around the park name, so the preview shows it bold the first time, as the email does
    const emailBody = email.body || '';
    const boldText = email.boldText || '';
    let bodyBefore = emailBody;
    let bodyBold = '';
    let bodyAfter = '';
    let boldPosition = -1;
    if (boldText !== '') {
      boldPosition = emailBody.indexOf(boldText);
    }
    if (boldPosition >= 0) {
      bodyBefore = emailBody.slice(0, boldPosition);
      bodyBold = boldText;
      bodyAfter = emailBody.slice(boldPosition + boldText.length);
    }

    return (
      <>
        {/* What happens next */}
        <ol className="mb-4">
          <li>Parki emails {application} asking them to give Parki access to {park.parkName}'s data.</li>
          <li>{application} replies to you, with Parki support copied in.</li>
          <li>Parki support finishes the setup and the park shows as complete.</li>
        </ol>

        {/* Warn when the park is already set up */}
        {preview.wasComplete && (
          <div className="alert alert-warning" role="status">{WAS_COMPLETE_WARNING}</div>
        )}

        {/* Tell the tester that only they will get the email */}
        {preview.testMode && (
          <div className="alert alert-info" role="status">
            Test mode: the email goes only to you, marked [TEST]. Below is what would go out live.
          </div>
        )}

        {/* The email, read-only */}
        <div className="integrations-email-preview mb-4">
          <dl className="row mb-2">
            <dt className="col-sm-2">To</dt>
            <dd className="col-sm-10">{toList}</dd>
            <dt className="col-sm-2">Cc</dt>
            <dd className="col-sm-10">{ccList}</dd>
            <dt className="col-sm-2">Subject</dt>
            <dd className="col-sm-10">{email.subject}</dd>
          </dl>
          <pre className="integrations-email-body mb-0">
            {bodyBefore}
            {bodyBold !== '' && <strong>{bodyBold}</strong>}
            {bodyAfter}
          </pre>
        </div>

        {/* Optional note */}
        <label htmlFor="pms-note" className="form-label">Note to {application} (optional)</label>
        <textarea
          id="pms-note"
          className="form-control"
          rows="3"
          maxLength={NOTE_MAX_LENGTH}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder={`For example, your ${application} client id`}
        ></textarea>
        <div className="form-text">Added to the end of the email.</div>
      </>
    );
  };

  // ---------------------------------------------------------------- #
  // Step 3: done
  // ---------------------------------------------------------------- #

  /**
   * Draw the confirmation, or the earlier request for a park already in review.
   */
  const renderDoneStep = () => {
    // Just sent from this wizard
    if (sendResult) {
      return (
        <>
          <div className="alert alert-success" role="status">
            Request sent. We've copied you and Parki support. Your PMS provider will reply to you; once they have, Parki support finishes the setup.
          </div>
          {sendResult.testMode && (
            <p className="text-body-secondary">Test mode: the email went only to you.</p>
          )}
        </>
      );
    }

    // Opened on a park that is already in review
    return (
      <>
        <p>A {currentApplication || 'PMS'} access request has already been sent for {park.parkName}. Parki support finishes the setup once your PMS provider replies.</p>
        {pmsCell.onboardNote && (
          <p className="text-body-secondary">{pmsCell.onboardNote}</p>
        )}
        <button type="button" className="btn btn-label-primary" onClick={handleSendAgain}>
          Send again
        </button>
      </>
    );
  };

  // ---------------------------------------------------------------- #
  // Footer button for each step
  // ---------------------------------------------------------------- #

  // Step 1 moves on once a PMS is chosen; step 2 sends; step 3 closes
  let primaryLabel = 'Next';
  let onPrimary = () => goToStep(2);
  let primaryDisabled = !application;
  if (step === 2) {
    primaryLabel = 'Send request';
    onPrimary = handleSend;
    primaryDisabled = !preview;
  } else if (step === 3) {
    primaryLabel = 'Close';
    onPrimary = handleClose;
    primaryDisabled = false;
  }

  // Unsaved work: a typed note, or a PMS picked but not yet sent
  const pickedButNotSent = application !== currentApplication && !sendResult;
  const dirty = note.trim() !== '' || pickedButNotSent;

  // ---------------------------------------------------------------- #
  // Render
  // ---------------------------------------------------------------- #

  return (
    <WizardFrame
      title="PMS"
      register={register}
      parkiId={park.parkiId}
      products={['pms']}
      onNotNeededChange={() => setChanged(true)}
      steps={PMS_STEPS}
      step={step}
      maxReachedStep={maxReachedStep}
      onStepChange={goToStep}
      error={error}
      dirty={dirty}
      onClose={handleClose}
      primaryLabel={primaryLabel}
      onPrimary={onPrimary}
      primaryDisabled={primaryDisabled}
      busy={busy}
      hideBack={step === 3}
    >
      {step === 1 && renderChooseStep()}
      {step === 2 && renderRequestStep()}
      {step === 3 && renderDoneStep()}
    </WizardFrame>
  );
};

export default PmsWizard;
